import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useEnquiry } from "@/context/EnquiryContext";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Check, MessageCircle, Phone, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/site/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { add, items } = useEnquiry();
  const s = useSettings();
  useEffect(() => { api.get(`/products/${slug}`).then(r => setP(r.data)); }, [slug]);
  if (!p) return <div className="container-x py-16">Loading...</div>;
  const inList = items.some(i => i.product_id === p.id);
  const wa = s.whatsapp_primary || "9513951318";
  const phone = s.phone_primary || "9513951318";
  const waMsg = encodeURIComponent(`Hello REXTECH IT SOLUTION, I am interested in ${p.name} (Qty: ${qty}). Please share availability and wholesale price.`);
  return (
    <div className="bg-white">
      <Seo title={p.seo_title || `${p.name} | REXTECH IT SOLUTION`} description={p.seo_description || p.description?.slice(0, 160)}/>
      <div className="container-x py-6">
        <Link to="/products" className="text-sm text-slate-500 inline-flex items-center hover:text-[#EA580C]"><ChevronLeft className="w-4 h-4"/> Back to Products</Link>
      </div>
      <div className="container-x pb-12 grid lg:grid-cols-2 gap-10">
        <div className="bg-[#F8FAFC] rounded-md border border-[#E2E8F0] p-4">
          <img src={p.images?.[0]} alt={p.name} className="w-full aspect-square object-cover rounded-md"/>
        </div>
        <div>
          <div className="flex gap-2 mb-2">
            {p.featured && <Badge className="bg-[#EA580C]">Featured</Badge>}
            {p.new_arrival && <Badge className="bg-[#1E3A8A]">New Arrival</Badge>}
            <Badge variant="outline">{p.condition}</Badge>
          </div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">{p.name}</h1>
          <div className="text-sm text-slate-500 mt-2">SKU: {p.sku}</div>
          <div className="mt-4 text-slate-700">{p.description}</div>
          <div className="mt-6 bg-[#FFF7ED] border border-[#EA580C]/30 rounded-md p-4">
            <div className="text-sm text-slate-600">Wholesale Price</div>
            <div className="text-2xl font-heading font-extrabold text-[#EA580C]">Request Latest Price</div>
            <div className="text-xs text-slate-500 mt-1">Availability & pricing confirmed on enquiry.</div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center border border-[#E2E8F0] rounded-md">
              <button className="px-3 py-2" onClick={() => setQty(q => Math.max(1, q - 1))}>-</button>
              <Input type="number" min={1} value={qty} onChange={e => setQty(Math.max(1, parseInt(e.target.value)||1))} className="w-16 text-center border-0"/>
              <button className="px-3 py-2" onClick={() => setQty(q => q + 1)}>+</button>
            </div>
            <Button className="btn-orange" onClick={() => { add(p, qty); toast.success("Added to enquiry"); }} data-testid="pdp-add-enquiry">
              {inList ? <Check className="w-4 h-4 mr-1"/> : <Plus className="w-4 h-4 mr-1"/>} Add to Enquiry
            </Button>
            <a href={`https://wa.me/91${wa}?text=${waMsg}`} target="_blank" rel="noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#1EBE5A]"><MessageCircle className="w-4 h-4 mr-1"/> WhatsApp Enquiry</Button>
            </a>
            <a href={`tel:${phone}`}><Button variant="outline" className="btn-outline-navy"><Phone className="w-4 h-4 mr-1"/> Call Now</Button></a>
          </div>
          <div className="mt-8">
            <h3 className="font-heading font-bold text-lg mb-2">Specifications</h3>
            <div className="border border-[#E2E8F0] rounded-md overflow-hidden">
              {Object.entries(p.specifications || {}).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 border-b last:border-b-0 border-[#E2E8F0]">
                  <div className="bg-[#F8FAFC] px-4 py-2 text-sm font-semibold">{k}</div>
                  <div className="col-span-2 px-4 py-2 text-sm">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
